import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { AnimatedSection } from 'remote-com-shared/style/motion';
import styled from 'styled-components';

import { fieldHasContent } from '@/src/domains/prismic/helpers';
import { AnimatedSectionWrapper, defaultAnimation } from '@/src/style/motion';

export const SectionWrapper = styled.section`
  ${fluidProp(['padding-top', 'padding-bottom'], '80px', '130px')};
`;

export const WhyRemoteLabel = styled.p`
  text-align: center;
  ${({ theme }) => theme.typography.subHead};
  text-transform: uppercase;
  color: #00183e;
`;

export const TitleComponent = styled.div`
  && {
    ${({ theme }) => theme.typography.scale2};
    text-align: center;
    margin-bottom: 20px;
    font-weight: 500;
    margin-top: 16px;

    > * {
      font-family: var(--typography-fontBossa);
      font-size: inherit;
      font-weight: inherit;
      margin: inherit;
    }
  }
`;

export const DescriptionComponent = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  color: var(--colors-bayoux);
  text-align: center;
  max-width: 780px;
  margin: 0 auto;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const ReasonsList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  row-gap: 29px;
  column-gap: 24px;
`;

export const ReasonEntry = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 16px;
  width: 45%;
  min-width: 400px;
  border-radius: 24px;
  background-color: #f5f9ff;
  box-sizing: border-box;
  padding: 32px;

  ${defaultAnimation}
`;

export const ReasonIllustration = styled.div``;

export const ReasonTitle = styled.div`
  && {
    ${({ theme }) => theme.typography.titleSM};
    font-family: var(--typography-fontBossa);

    ${ReasonIllustration} + & {
      margin-top: 32px;
    }

    > * {
      font-family: inherit;
      font-weight: inherit;
      margin: 0;
    }
  }
`;

export const ReasonDescription = styled.div`
  ${({ theme }) => theme.typography.body}
  color: var(--colors-darkBlue);
  opacity: 0.65;
`;

export const SectionHeader = styled.div`
  margin-bottom: 100px;
`;

export function Entry({
  reason_title: reasonTitle,
  reason_description: reasonDescription,
  illustration,
}) {
  return (
    <AnimatedSection ComponentName={ReasonEntry}>
      {illustration && (
        <ReasonIllustration>
          <Image
            src={illustration.url}
            alt={illustration.alt || ''}
            width={illustration.dimensions.width}
            height={illustration.dimensions.height}
            quality="100"
          />
        </ReasonIllustration>
      )}
      {fieldHasContent(reasonTitle) && (
        <ReasonTitle>
          <PrismicRichText field={reasonTitle} />
        </ReasonTitle>
      )}
      {fieldHasContent(reasonDescription) && (
        <ReasonDescription>
          <PrismicRichText field={reasonDescription} />
        </ReasonDescription>
      )}
    </AnimatedSection>
  );
}

export function NewWhyRemoteSection({ label, title, description, list = [] }) {
  return (
    <SectionWrapper id="why-remote">
      <ContentContainer>
        <AnimatedSection ComponentName={AnimatedSectionWrapper}>
          <SectionHeader>
            {fieldHasContent(label) && <WhyRemoteLabel>{label}</WhyRemoteLabel>}
            {fieldHasContent(title) && (
              <TitleComponent>
                <PrismicRichText field={title} />
              </TitleComponent>
            )}
            {fieldHasContent(description) && (
              <DescriptionComponent>
                <PrismicRichText field={description} />
              </DescriptionComponent>
            )}
          </SectionHeader>
        </AnimatedSection>
        <ReasonsList>
          {list.map((listItem, index) => (
            <Entry {...listItem} key={index} />
          ))}
        </ReasonsList>
      </ContentContainer>
    </SectionWrapper>
  );
}
